import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../api/api";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import { TrendingUp, Clock, Sunrise, Plane, BarChart3, Activity, Tag } from "lucide-react";
import { T, RAW } from "../../components/em/ui";

const AttendanceAnalytics = () => {
  const token = localStorage.getItem("token");
  const [kpis, setKpis] = useState({});
  const [trend, setTrend] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState([]);
  const [loading, setLoading] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/attendance/analytics`, authHeader);
        setKpis(res.data.kpis || {});
        setTrend(res.data.attendanceTrend || []);
        setWeeklyHours(res.data.weeklyHours || []);
      } catch (err) {
        console.error("Analytics fetch error", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.fg5 }}>Loading analytics…</div>;

  const card = { background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowSm };
  const kpiCards = [
    { label: "Attendance Rate", v: `${kpis.attendanceRate ?? 0}%`, Icon: TrendingUp, c: T.success600, bg: T.success50 },
    { label: "Late Percentage", v: `${kpis.latePercentage ?? 0}%`, Icon: Clock, c: "#b45309", bg: "rgba(251,191,36,0.12)" },
    { label: "Early Birds", v: kpis.earlyBirds ?? 0, Icon: Sunrise, c: T.purple, bg: T.purple50 },
    { label: "On Leave Today", v: kpis.onLeaveToday ?? 0, Icon: Plane, c: T.info600, bg: T.info50 },
  ];
  const legend = [
    { label: "Present", color: T.success600 },
    { label: "Late", color: "#f59e0b" },
    { label: "Absent", color: T.danger },
    { label: "Early Bird", color: T.purple },
    { label: "On Leave", color: T.info },
  ];

  const ChartHeader = ({ Icon, title, sub }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: T.orange50, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={15} color={T.orange} />
      </div>
      <div>
        <div style={{ font: `700 14px ${T.font}`, color: T.fg1 }}>{title}</div>
        <div style={{ font: `400 11px ${T.font}`, color: T.fg7 }}>{sub}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        {kpiCards.map((k) => (
          <div key={k.label} style={{ ...card, padding: "18px 20px" }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: k.bg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <k.Icon size={18} color={k.c} />
            </div>
            <div style={{ font: `800 26px/1 ${T.font}`, color: T.fg1, letterSpacing: "-0.03em", fontVariantNumeric: "tabular-nums" }}>{k.v}</div>
            <div style={{ font: `500 12px ${T.font}`, color: T.fg4, marginTop: 6 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16 }}>
        <div style={{ ...card, padding: "18px 20px" }}>
          <ChartHeader Icon={BarChart3} title="Attendance Trend" sub="Present · Late · Absent by day" />
          {trend.length === 0 ? (
            <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1", font: `400 13px ${T.font}` }}>No attendance data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={trend} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={RAW.divider} vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: RAW.fg7 }} />
                <YAxis tick={{ fontSize: 11, fill: RAW.fg7 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${T.border}` }} />
                <Bar dataKey="present" fill={RAW.success600} radius={[4, 4, 0, 0]} />
                <Bar dataKey="late" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="absent" fill={RAW.danger} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={{ ...card, padding: "18px 20px" }}>
          <ChartHeader Icon={Activity} title="Weekly Average Hours" sub="Average hours worked per day" />
          {weeklyHours.length === 0 ? (
            <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1", font: `400 13px ${T.font}` }}>No working hours recorded</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={weeklyHours} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={RAW.divider} vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: RAW.fg7 }} />
                <YAxis tick={{ fontSize: 11, fill: RAW.fg7 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${T.border}` }} />
                <Line dataKey="hours" stroke={RAW.orange} strokeWidth={2.5} dot={{ r: 3, fill: RAW.orange }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={{ ...card, padding: "18px 20px" }}>
        <ChartHeader Icon={Tag} title="Status Legend" sub="What each status colour means" />
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
          {legend.map((l) => (
            <span key={l.label} style={{ display: "inline-flex", alignItems: "center", gap: 7, font: `500 12.5px ${T.font}`, color: T.fg4 }}>
              <span style={{ width: 9, height: 9, borderRadius: 9999, background: l.color }} /> {l.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AttendanceAnalytics;
